import React from 'react';
import {
  Card,
  CardTitle,
  Container,
  Input,
  FormFeedback,
} from 'reactstrap';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useHistory, useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import AppBreadcrumb from 'components/App/Breadcrumb';
import AppButton from 'components/App/Button';
import AppInputValidation from 'components/App/InputValidation';
import Loading from 'components/App/Loading';
import AppMeta from 'components/App/Meta';
import SelectValidation from 'components/App/SelectValidation';

import {
  LOCATIONS,
  themeColor,
  ROOM_TYPE,
  storageKey,
  LOGIN_TYPE,
} from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import MODAL_TYPE from 'constants/modalType';
import { addPlacePriceTypes } from 'utils/validate';
import formatDate from 'utils/formatDate';

import styles from './EditPriceType.module.scss';

const optionPricingStrategy = [
  {
    value: 'NEW_PRICE',
    label: 'Cài đặt như một loại giá mới',
  },
  {
    value: 'BASE_ON_STANDARD',
    label: 'Dựa trên Giá cơ bản',
  },
];

const optionPriceAdjustmentType = [
  {
    value: 'PERCENT',
    label: '%',
  },
  {
    value: 'AMOUNT',
    label: 'VND',
  },
];

function EditPriceType(props) {
  const {
    isLoadingGetRoomsOfPlace,
    isLoadingGetPlaceDetail,
    isLoadingGetPlacePriceTypes,

    roomsOfPlace,
    placePriceTypes,
    placeDetail,

    actionGetRoomsOfPlace,
    actionGetPlaceDetail,
    actionAddPlacePriceTypes,
    actionGetPlacePriceTypes,
    actionEditPlacePriceTypes,
    actionOpenModalCreateSuccess,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');
  const priceId = new URLSearchParams(location.search).get('id');

  const priceTypeDetails = placePriceTypes.find((item) => item._id === priceId);

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const cancelBookingPolicyList = React.useMemo(
    () => placeDetail?.cancelBookingPolicy ?? [],
    [placeDetail],
  );

  const existingCancelPolicyId = React.useMemo(() => {
    const currentPolicy = priceTypeDetails?.cancelBookingPolicy;
    return currentPolicy?._id ?? '';
  }, [priceTypeDetails?.cancelBookingPolicy]);

  const cancelPolicyInitializedRef = React.useRef(false);

  const [isIncludedMeals, setIsIncludedMeals] = React.useState(false);

  const breadcrumbList = [
    {
      name: 'Giá và tình trạng phòng trống',
      isActive: false,
      link: LOCATIONS.LOCATION_LIST,
    },
    {
      name: 'Loại giá',
      isActive: false,
      link: LOCATIONS.LOCATION_DETAIL.LIST_PRICE_TYPE,
    },
    {
      name: `${priceId ? 'Chỉnh sửa loại giá hoàn toàn linh động' : 'Thêm loại giá hoàn toàn linh động'}`,
      isActive: true,
      link: '',
    },
  ];

  const onCallBackSave = React.useCallback(() => {
    if (priceId) {
      history.push(`${LOCATIONS.LOCATION_DETAIL.LIST_PRICE_TYPE}?place=${placeId}`);
      actionOpenModalCreateSuccess(MODAL_TYPE.EDIT_PRICE_SUCCESS);
    } else {
      history.push(`${LOCATIONS.LOCATION_DETAIL.LIST_PRICE_TYPE}?place=${placeId}`);
      actionOpenModalCreateSuccess(MODAL_TYPE.CREATE_PRICE_SUCCESS);
    }
  }, [actionOpenModalCreateSuccess, history, placeId, priceId]);

  const onCancel = React.useCallback(() => {
    history.push(`${LOCATIONS.LOCATION_DETAIL.LIST_PRICE_TYPE}?place=${placeId}`);
  }, [history, placeId]);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: priceId ? priceTypeDetails?.name : '',
      cancelBookingPolicy: priceId ? existingCancelPolicyId : '',
      includeMeals: priceId ? priceTypeDetails?.includeMeals : [],
      rooms: priceId ? priceTypeDetails?.rooms.map((room) => room._id) : [],
      pricingStrategy: priceId && priceTypeDetails?.pricingStrategy
        ? priceTypeDetails?.pricingStrategy
        : optionPricingStrategy[0].value,
      priceAdjustmentType: priceId && priceTypeDetails?.priceAdjustment ? priceTypeDetails?.priceAdjustment?.type : 'PERCENT',
      priceAdjustmentValue: priceId && priceTypeDetails?.priceAdjustment
        ? priceTypeDetails?.priceAdjustment?.value : 0,
    },

    validationSchema: Yup.object({
      name: addPlacePriceTypes.name,
      rooms: addPlacePriceTypes.rooms,
      includeMeals: addPlacePriceTypes.includeMeals,
      priceAdjustmentValue: addPlacePriceTypes.priceAdjustmentValue,
    }),

    onSubmit: (values) => {
      const result = {
        name: values.name,
        includeMeals: values.includeMeals,
        cancelBookingPolicy: values.cancelBookingPolicy,
        rooms: values.rooms,
        pricingStrategy: values.pricingStrategy,
        priceAdjustment: {
          type: values.priceAdjustmentType,
          value: values.priceAdjustmentValue,
        },
        placeId,
        priceId,
        onCallBackSave,
      };

      if (priceId) {
        actionEditPlacePriceTypes(result);
      } else {
        actionAddPlacePriceTypes(result);
      }
    },
  });

  const nameSectionHeaderNumber = React.useMemo(() => {
    if (priceTypeDetails?.isDefault) {
      return 4;
    }

    if (validation.values.pricingStrategy === 'BASE_ON_STANDARD') {
      return 6;
    }

    return 5;
  }, [priceTypeDetails?.isDefault, validation.values.pricingStrategy]);

  const onToggleCheckedRoom = React.useCallback((event) => {
    const roomId = event.target.id;
    const isChecked = event.target.checked;

    if (isChecked) {
      validation.setFieldValue('rooms', [...validation.values.rooms, roomId]);
    } else {
      validation.setFieldValue(
        'rooms',
        validation.values.rooms.filter((item) => item !== roomId),
      );
    }
  }, [validation]);

  const onCheckedAllRooms = React.useCallback(() => {
    const allRoomIds = roomsOfPlace.map((item) => item._id);
    validation.setFieldValue('rooms', allRoomIds);
  }, [validation, roomsOfPlace]);

  const setCancelBookingPolicy = React.useCallback((policyId, shouldValidate = true) => {
    validation.setFieldValue('cancelBookingPolicy', policyId, shouldValidate);
  }, [validation]);

  React.useEffect(() => {
    cancelPolicyInitializedRef.current = false;
  }, [placeId, priceId]);

  React.useEffect(() => {
    if (!cancelBookingPolicyList?.length) return;
    if (priceId && !priceTypeDetails) return;
    if (cancelPolicyInitializedRef.current) return;

    const appliedPolicy = cancelBookingPolicyList.find((policy) => policy?.isApplied);
    const appliedPolicyId = appliedPolicy?._id ?? '';

    let selectedPolicyId = appliedPolicyId;

    if (priceId && existingCancelPolicyId) {
      const isExistingPolicyInList = cancelBookingPolicyList.some(
        (policy) => policy?._id === existingCancelPolicyId,
      );
      if (isExistingPolicyInList) {
        selectedPolicyId = existingCancelPolicyId;
      }
    }

    if (selectedPolicyId) {
      setCancelBookingPolicy(selectedPolicyId, false);
      cancelPolicyInitializedRef.current = true;
    }
  }, [
    cancelBookingPolicyList,
    priceId,
    priceTypeDetails,
    existingCancelPolicyId,
    setCancelBookingPolicy,
  ]);

  const onChangeMealIncluded = React.useCallback((event) => {
    const selectedMeal = event.target.id;
    const updatedMeals = [...validation.values.includeMeals];

    const filteredMeals = updatedMeals.filter((meal) => !meal.includes('_'));

    if (filteredMeals.includes(selectedMeal)) {
      filteredMeals.splice(filteredMeals.indexOf(selectedMeal), 1);
    } else {
      filteredMeals.push(selectedMeal);
    }

    validation.setFieldValue('includeMeals', filteredMeals, true);
  }, [validation]);

  const onChangeMeals = React.useCallback(() => {
    setIsIncludedMeals(!isIncludedMeals);

    validation.setFieldValue('includeMeals', isIncludedMeals ? [] : ['_']);
  }, [isIncludedMeals, validation]);

  const onChangePricingStrategy = React.useCallback((event) => {
    validation.setFieldValue('pricingStrategy', event.target.value, true);
  }, [validation]);

  const onChangePriceAdjustmentType = React.useCallback((e) => {
    validation.setFieldValue('priceAdjustmentType', e.value, true);
  }, [validation]);

  React.useEffect(() => {
    actionGetRoomsOfPlace({ placeId });
  }, [actionGetRoomsOfPlace, placeId]);

  React.useEffect(() => {
    actionGetPlacePriceTypes({ placeId });
  }, [actionGetPlacePriceTypes, placeId]);

  React.useEffect(() => {
    actionGetPlaceDetail({ placeId });
  }, [actionGetPlaceDetail, placeId]);

  return (
    <form onSubmit={validation.handleSubmit} className={`page-content ${styles.Page}`}>
      <Loading
        loaderOnPage
        loading={
          isLoadingGetRoomsOfPlace || isLoadingGetPlacePriceTypes || isLoadingGetPlaceDetail
        }
      />

      <AppMeta
        name={`${priceId ? 'Chỉnh sửa loại giá hoàn toàn linh động' : 'Thêm loại giá hoàn toàn linh động'}`}
      />

      <Container fluid>
        <AppBreadcrumb
          pageTitle={`${priceId ? 'Chỉnh sửa loại giá hoàn toàn linh động' : 'Thêm loại giá hoàn toàn linh động'}`}
          breadcrumbList={breadcrumbList}
        />

        <div className="CContainer">
          <div className="CContainer__Body">
            <Card className="Section">
              <CardTitle className="Section__Header">
                <div className="Section__Header__Number">1</div>

                <span className="Section__Header__Title">Bạn muốn sử dụng chính sách hủy phòng nào cho loại giá này?</span>
              </CardTitle>

              <div className="Section__Body">
                <div className="BtnRadioWrapper">
                  {cancelBookingPolicyList.map((policy) => {
                    const policyId = policy?._id;
                    if (!policyId) return null;

                    const isNoRefund = policy.freeBeforeDays === 0;
                    const currentCancelPolicyId = validation.values.cancelBookingPolicy;
                    const isChecked = currentCancelPolicyId === policyId;
                    const label = isNoRefund
                      ? 'Không hoàn huỷ'
                      : `Linh động ${policy.freeBeforeDays} ngày (Giai đoạn áp dụng: ${formatDate(policy.startAt)} - ${formatDate(policy.endAt)})`;

                    return (
                      <div className="Box" key={policyId}>
                        <input
                          className="InputRadio"
                          type="radio"
                          id={`cancel_policy_${policyId}`}
                          name="cancelBookingPolicy"
                          checked={isChecked}
                          value={policyId}
                          onChange={() => setCancelBookingPolicy(policyId, true)}
                        />
                        <label htmlFor={`cancel_policy_${policyId}`}>{label}</label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>

            <Card className="Section">
              <CardTitle className="Section__Header">
                <div className="Section__Header__Number">2</div>

                <span className="Section__Header__Title">Bạn có muốn bao gồm bữa ăn trong loại giá này?</span>
              </CardTitle>

              <div className="Section__Body">
                <div className="BtnRadioWrapper">
                  <div className="Box">
                    <input
                      className="InputRadio"
                      type="radio"
                      id="NotIncluded"
                      name="meal"
                      checked={priceId
                        ? validation.values.includeMeals?.length === 0 : !isIncludedMeals}
                      value={0}
                      onChange={onChangeMeals}
                    />

                    <label htmlFor="NotIncluded">Không</label>
                  </div>

                  <div className="Box">
                    <input
                      className="InputRadio"
                      type="radio"
                      id="included"
                      name="meal"
                      checked={priceId
                        ? validation.values.includeMeals?.length !== 0 : isIncludedMeals}
                      value={1}
                      onChange={onChangeMeals}
                    />

                    <label htmlFor="included">Có, thêm lựa chọn bữa ăn</label>
                  </div>
                </div>

                <div className="Section__Body--SubChoose">
                  <div className="Section__MealsBlock">
                    <div className="Section__MealsBlock--CheckBox">
                      <Input
                        type="checkbox"
                        disabled={priceId
                          ? validation.values.includeMeals?.length === 0 : !isIncludedMeals}
                        className="Checkbox__Input"
                        id="BREAKFAST"
                        onChange={onChangeMealIncluded}
                        checked={validation.values.includeMeals?.includes('BREAKFAST')}
                      />
                      <label htmlFor="BREAKFAST" className="Checkbox__Name">
                        Bữa sáng
                      </label>
                    </div>

                    <div className="Section__MealsBlock--CheckBox">
                      <Input
                        type="checkbox"
                        disabled={priceId
                          ? validation.values.includeMeals?.length === 0 : !isIncludedMeals}
                        className="Checkbox__Input"
                        id="LUNCH"
                        onChange={onChangeMealIncluded}
                        checked={validation.values.includeMeals?.includes('LUNCH')}
                      />
                      <label htmlFor="LUNCH" className="Checkbox__Name">
                        Bữa trưa
                      </label>
                    </div>

                    <div className="Section__MealsBlock--CheckBox">
                      <Input
                        type="checkbox"
                        disabled={priceId
                          ? validation.values.includeMeals?.length === 0 : !isIncludedMeals}
                        className="Checkbox__Input"
                        id="DINNER"
                        onChange={onChangeMealIncluded}
                        checked={validation.values.includeMeals?.includes('DINNER')}
                      />
                      <label htmlFor="DINNER" className="Checkbox__Name">
                        Bữa tối
                      </label>
                    </div>
                  </div>

                  {validation.errors?.includeMeals && (
                  <FormFeedback type="invalid" className="d-block font-size-12 mt-3">
                    {validation.errors.includeMeals}
                  </FormFeedback>
                  )}
                </div>
              </div>
            </Card>

            <Card className="Section">
              <CardTitle className="Section__Header">
                <div className="Section__Header__Number">3</div>

                <span className="Section__Header__Title">Bạn muốn áp dụng giá cho loại phòng nào?</span>
              </CardTitle>

              <div className="Section__Body">
                <div className="Section_RoomBlock">
                  <div className="Section__RoomBlock--CheckBox">
                    {
                  roomsOfPlace.map((item) => (
                    <div className="Checkbox Checkbox--RoomBlock" key={item._id}>
                      <Input
                        type="checkbox"
                        checked={validation.values.rooms?.includes(item._id)}
                        className="Checkbox__Input"
                        value={item._id}
                        id={item._id}
                        onChange={onToggleCheckedRoom}
                      />

                      <label htmlFor={item._id} className="Checkbox__Name">
                        {item.name || ROOM_TYPE[item.roomName].label}
                      </label>
                    </div>
                  ))
                }
                  </div>

                  <button
                    className="btn-minimal blue-100 mt-24"
                    type="button"
                    onClick={onCheckedAllRooms}
                  >
                    Chọn tất cả
                  </button>

                  {validation.errors?.rooms && (
                  <FormFeedback type="invalid" className="d-block font-size-12 mt-3">
                    {validation.errors.rooms}
                  </FormFeedback>
                  )}
                </div>
              </div>
            </Card>

            {!priceTypeDetails?.isDefault && (
              <Card className="Section">
                <CardTitle className="Section__Header">
                  <div className="Section__Header__Number">4</div>

                  <span className="Section__Header__Title">Quý vị muốn quản lý loại giá này như thế nào?</span>
                </CardTitle>

                <div className="Section__Body">
                  <div className="BtnRadioWrapper">
                    {optionPricingStrategy.map((option) => (
                      <div className="Box" key={option.value}>
                        <input
                          className="InputRadio"
                          type="radio"
                          id={option.value}
                          name="pricingStrategy"
                          checked={validation.values.pricingStrategy === option.value}
                          value={option.value}
                          onChange={onChangePricingStrategy}
                        />
                        <label htmlFor={option.value}>{option.label}</label>
                      </div>
                    ))}
                  </div>

                  {validation.errors?.pricingStrategy && (
                    <FormFeedback type="invalid" className="d-block font-size-12 mt-3">
                      {validation.errors.pricingStrategy}
                    </FormFeedback>
                  )}
                </div>
              </Card>
            )}

            {validation.values.pricingStrategy === 'BASE_ON_STANDARD' && (
              <Card className="Section">
                <CardTitle className="Section__Header">
                  <div className="Section__Header__Number">5</div>

                  <span className="Section__Header__Title">Quý vị muốn thiết lập giá này chêch lệch so với Standard Rate bao nhiêu</span>
                </CardTitle>

                <div className="Section__Body--PriceAdjustment">
                  <AppInputValidation
                    name="priceAdjustmentValue"
                    type="number"
                    placeholder="Vui lòng nhập mức chêch lệch"
                    validation={validation}
                    value={validation.values.priceAdjustmentValue}
                  />

                  <SelectValidation
                    placeholder="Đơn vị"
                    options={optionPriceAdjustmentType}
                    value={validation.values.priceAdjustmentType}
                    validation={validation}
                    onChange={onChangePriceAdjustmentType}
                  />
                </div>
              </Card>
            )}

            <Card className="Section mb-0">
              <CardTitle className="Section__Header">
                <div className="Section__Header__Number">{nameSectionHeaderNumber}</div>

                <span className="Section__Header__Title">Bạn có muốn đặt tên cho loại giá này là gì?</span>
              </CardTitle>

              <div className="Section__Body">
                <AppInputValidation
                  name="name"
                  type="text"
                  placeholder="Vui lòng nhập tên loại giá"
                  validation={validation}
                  value={validation.values.name}
                />
              </div>
            </Card>
          </div>

          <div className={isExtranet ? styles.StickyButtonsExtranet : styles.StickyButtons}>
            <div className={styles.ButtonWrap}>
              <AppButton color={themeColor.SECONDARY} onClick={onCancel}>
                Hủy
              </AppButton>

              <AppButton
                type="submit"
              >
                {priceId ? 'Cập nhật' : 'Thêm'}
              </AppButton>
            </div>
          </div>
        </div>
      </Container>
    </form>
  );
}

EditPriceType.propTypes = {
  isLoadingGetRoomsOfPlace: PropTypes.bool,
  isLoadingGetPlaceDetail: PropTypes.bool,
  isLoadingGetPlacePriceTypes: PropTypes.bool,

  roomsOfPlace: PropTypes.instanceOf(Array),
  placePriceTypes: PropTypes.instanceOf(Array),
  placeDetail: PropTypes.instanceOf(Object),

  actionGetRoomsOfPlace: PropTypes.func,
  actionGetPlaceDetail: PropTypes.func,
  actionAddPlacePriceTypes: PropTypes.func,
  actionGetPlacePriceTypes: PropTypes.func,
  actionEditPlacePriceTypes: PropTypes.func,
  actionOpenModalCreateSuccess: PropTypes.func,
};

EditPriceType.defaultProps = {
  isLoadingGetRoomsOfPlace: false,
  isLoadingGetPlaceDetail: false,
  isLoadingGetPlacePriceTypes: false,

  roomsOfPlace: [],
  placePriceTypes: INITIAL_VALUE.ARRAY,
  placeDetail: INITIAL_VALUE.OBJECT,

  actionGetRoomsOfPlace: INITIAL_VALUE.FUNCTION,
  actionGetPlaceDetail: INITIAL_VALUE.FUNCTION,
  actionAddPlacePriceTypes: INITIAL_VALUE.FUNCTION,
  actionGetPlacePriceTypes: INITIAL_VALUE.FUNCTION,
  actionEditPlacePriceTypes: INITIAL_VALUE.FUNCTION,
  actionOpenModalCreateSuccess: INITIAL_VALUE.FUNCTION,
};

export default EditPriceType;
